<template>
  <div class="selfsvc-page">
    <div class="page-header">
      <div class="page-avatar"><v-icon size="22" color="#fff">mdi-cash-fast</v-icon></div>
      <div class="page-header-text">
        <h1 class="page-title">{{ $t('selfService.queueTitle') }}</h1>
      </div>
      <button class="action-pill action-pill--neutral" @click="load" :disabled="loading">
        <v-icon size="14">mdi-refresh</v-icon>{{ $t('dailyPayouts.refresh') }}
      </button>
    </div>

    <v-alert
      v-if="manualMode"
      type="info"
      dense
      text
      class="mb-3"
      icon="mdi-hand-coin"
    >
      Tryb ręczny: „Akceptuj" tworzy potrącenie i oznacza wypłatę jako zrealizowaną.
      Przelew BLIK wykonaj ręcznie ze swojej aplikacji bankowej na numer telefonu kierowcy.
    </v-alert>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="queue"
        :items-per-page="50"
        :no-data-text="$t('selfService.empty')"
        dark
        dense
      >
        <template v-slot:[`item.driver`]="{ item }">
          {{ item.driver.firstName }} {{ item.driver.lastName }}
        </template>
        <template v-slot:[`item.phone`]="{ item }">{{ item.driver.phone || '—' }}</template>
        <template v-slot:[`item.requestedAmount`]="{ item }">
          <strong class="warning--text">{{ (item.requestedAmountCents / 100).toFixed(2) }} zł</strong>
        </template>
        <template v-slot:[`item.availableAtRequest`]="{ item }">
          {{ (item.availableAtRequestCents / 100).toFixed(2) }} zł
        </template>
        <template v-slot:[`item.fee`]="{ item }">
          {{ (item.feeCents / 100).toFixed(2) }} zł
        </template>
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn x-small color="success" class="mr-2" @click="approve(item)">
            <v-icon x-small left>mdi-check</v-icon>{{ $t('selfService.approve') }}
          </v-btn>
          <v-btn x-small outlined color="error" @click="openReject(item)">
            <v-icon x-small left>mdi-close</v-icon>{{ $t('selfService.reject') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Reject dialog -->
    <v-dialog v-model="rejectDialog.open" max-width="500" dark>
      <v-card>
        <v-card-title class="headline">{{ $t('selfService.confirmReject') }}</v-card-title>
        <v-card-text>
          <div class="mb-3">
            <strong>{{ rejectDialog.item?.driver.firstName }} {{ rejectDialog.item?.driver.lastName }}</strong>
            —
            <span class="warning--text">{{ rejectDialog.item ? (rejectDialog.item.requestedAmountCents / 100).toFixed(2) : '' }} zł</span>
          </div>
          <v-textarea
            v-model="rejectDialog.reason"
            :label="$t('selfService.rejectReason')"
            outlined
            rows="3"
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rejectDialog.open = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" :loading="rejectDialog.submitting" @click="confirmReject">
            {{ $t('selfService.reject') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
const axios = require('axios');

export default {
  name: 'SelfServicePayoutsPage',
  data: () => ({
    queue: [],
    loading: false,
    manualMode: false,
    snackbar: { show: false, color: 'success', text: '' },
    rejectDialog: { open: false, item: null, reason: '', submitting: false },
  }),
  computed: {
    headers() {
      return [
        { text: this.$t('selfService.driver'), value: 'driver', sortable: false },
        { text: this.$t('selfService.phone'), value: 'phone' },
        { text: this.$t('selfService.requestedAmount'), value: 'requestedAmount', align: 'end' },
        { text: this.$t('selfService.availableAtRequest'), value: 'availableAtRequest', align: 'end' },
        { text: this.$t('selfService.fee'), value: 'fee', align: 'end' },
        { text: this.$t('selfService.createdAt'), value: 'createdAt' },
        { text: this.$t('common.actions'), value: 'actions', sortable: false, align: 'end' },
      ];
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleString(this.$i18n.locale === 'pl' ? 'pl-PL' : 'en-US');
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('self-service/admin/queue'), headers: this.authHeaders() });
        this.queue = (res.data && res.data.data) || [];
        try {
          const cfg = await axios({ method: 'get', url: this.apiUrl('payout-config'), headers: this.authHeaders() });
          this.manualMode = !!(cfg.data && cfg.data.data && cfg.data.data.selfServiceManualMode);
        } catch (_) { /* tryb tylko do etykiety — brak nie blokuje kolejki */ }
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: (e.response && e.response.data && e.response.data.message) || 'Error' };
      } finally {
        this.loading = false;
      }
    },
    async approve(item) {
      const amount = (item.requestedAmountCents / 100).toFixed(2);
      const msg = this.manualMode
        ? `Zrealizować ręcznie wypłatę ${amount} zł dla ${item.driver.firstName} ${item.driver.lastName}?\n\nSystem utworzy potrącenie i oznaczy jako wypłacone. BLIK wyślij ręcznie na numer ${item.driver.phone || '—'}.`
        : this.$t('selfService.confirmApprove');
      if (!confirm(msg)) return;
      try {
        await axios({
          method: 'post',
          url: this.apiUrl('self-service/admin/' + item.id + '/approve'),
          headers: this.authHeaders(),
        });
        this.snackbar = { show: true, color: 'success', text: this.$t('selfService.approved') };
        await this.load();
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: (e.response && e.response.data && e.response.data.message) || 'Error' };
      }
    },
    openReject(item) {
      this.rejectDialog = { open: true, item, reason: '', submitting: false };
    },
    async confirmReject() {
      this.rejectDialog.submitting = true;
      try {
        await axios({
          method: 'post',
          url: this.apiUrl('self-service/admin/' + this.rejectDialog.item.id + '/reject'),
          headers: this.authHeaders(),
          data: { reason: this.rejectDialog.reason || undefined },
        });
        this.snackbar = { show: true, color: 'success', text: this.$t('selfService.rejected') };
        this.rejectDialog.open = false;
        await this.load();
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: (e.response && e.response.data && e.response.data.message) || 'Error' };
      } finally {
        this.rejectDialog.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.selfsvc-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; }
.page-avatar { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 6px 18px rgba(41,98,255,0.4); }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; text-transform: capitalize; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.page-header > .action-pill { margin-left: auto; }
.action-pill { display: inline-flex; align-items: center; gap: 6px; background: #181C28; border: 1px solid rgba(255,255,255,0.08); color: #fff; padding: 8px 14px; border-radius: 10px; font-size: 12.5px; font-weight: 600; letter-spacing: -0.01em; cursor: pointer; transition: transform 0.12s ease, background 0.15s ease, border-color 0.15s ease; font-family: inherit; }
.action-pill:hover { transform: translateY(-1px); border-color: rgba(255,255,255,0.18); }
.action-pill .v-icon { color: inherit !important; }
.action-pill--neutral { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.12); color: #B0BEC5; }

.selfsvc-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.selfsvc-page >>> .v-data-table { background: transparent !important; color: #fff !important; }
.selfsvc-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.selfsvc-page >>> .v-data-table tbody tr { background: transparent !important; }
.selfsvc-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.selfsvc-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.selfsvc-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.selfsvc-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.selfsvc-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.selfsvc-page >>> .warning--text { color: #FFB74D !important; }
.selfsvc-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.selfsvc-page >>> .v-dialog .v-card { background: #232838 !important; }
</style>
